"use client";

import React from "react";
import { motion, AnimatePresence, HTMLMotionProps } from "framer-motion";

// Tailwind is assumed to be available globally.
// All variants are accessible via the `variant` prop.
// Accessible by default: role="status", aria-busy.

export type LoaderVariant =
  | "spinner"
  | "dots"
  | "bar"
  | "skeleton"
  | "overlay"
  | "button";

export type LoaderSize = "xs" | "sm" | "md" | "lg" | "xl";

const sizeMap: Record<LoaderSize, number> = {
  xs: 14,
  sm: 18,
  md: 24,
  lg: 32,
  xl: 40,
};

export interface LoaderProps extends Omit<HTMLMotionProps<"div">, "ref"> {
  /** Choose a built-in loader style */
  variant?: LoaderVariant;
  /** Controls visibility; when false, renders null by default */
  show?: boolean;
  /** Visual size for dot/spinner/bar; skeleton uses width/height props */
  size?: LoaderSize | number; // number interpreted as px
  /** Optional helper text shown next to/under loader */
  label?: string;
  /** For skeletons: width/height (any CSS size), rounded, and count for lines */
  width?: number | string;
  height?: number | string;
  rounded?: string; // e.g., 'rounded-lg'
  count?: number; // number of skeleton lines
  /** For overlay: make it fullscreen and optionally blur the backdrop */
  fullscreen?: boolean;
  blur?: boolean;
  /** For button variant: adapts to parent height and centers */
  asChild?: boolean; // if true, just returns the loader mark-up without outer wrapper
}

function px(v?: LoaderSize | number) {
  if (typeof v === "number") return v;
  if (!v) return sizeMap.md;
  return sizeMap[v];
}
const basePulse = {
  initial: { opacity: 0.3 },
  animate: { opacity: [0.3, 1, 0.3] },
  transition: {
    duration: 1.5,
    repeat: Infinity,
    times: [0, 0.5, 1],
  },
};

const Spinner: React.FC<Pick<LoaderProps, "size" | "label">> = ({
  size,
  label,
}) => {
  const d = px(size);
  return (
    <div className="flex items-center justify-center gap-3" role="status" aria-busy>
      <span
        className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent text-[#D97706]"
        style={{ width: d, height: d }}
      />
      {label ? (
        <span className="text-sm font-medium text-slate-600">{label}</span>
      ) : null}
    </div>
  );
};

const Dots: React.FC<Pick<LoaderProps, "size" | "label">> = ({
  size,
  label,
}) => {
  const d = Math.max(6, Math.round(px(size) / 3));
  return (
    <div className="flex items-center gap-3" role="status" aria-busy>
      <div className="flex items-end gap-1">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="block rounded-full bg-current"
            style={{ width: d, height: d }}
            initial={{ y: 0, opacity: 0.5 }}
            animate={{ y: [0, -6, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
      {label ? (
        <span className="text-sm text-muted-foreground">{label}</span>
      ) : null}
    </div>
  );
};

const Bar: React.FC<Pick<LoaderProps, "size" | "label">> = ({
  size,
  label,
}) => {
  const w = Math.max(120, px(size) * 6);
  const h = Math.max(4, Math.round(px(size) / 6));
  return (
    <div className="flex flex-col gap-2" role="status" aria-busy>
      <div
        className="relative overflow-hidden rounded-full bg-muted"
        style={{ width: w, height: h }}
      >
        <motion.span
          className="absolute inset-y-0 left-0 inline-block rounded-full bg-current"
          initial={{ x: -w }}
          animate={{ x: [-w, w] }}
          transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: [0.4, 0, 0.2, 1],
          }}
          style={{ width: w / 2 }}
        />
      </div>
      {label ? (
        <span className="text-xs text-muted-foreground">{label}</span>
      ) : null}
    </div>
  );
};

export const Skeleton: React.FC<
  Pick<LoaderProps, "width" | "height" | "rounded">
> = ({ width = "100%", height = 14, rounded = "rounded-lg" }) => (
  <motion.div
    {...basePulse}
    className={`bg-black ${rounded}`}
    style={{ width, height }}
    aria-hidden
  />
);

const SkeletonBlock: React.FC<Pick<LoaderProps, "count">> = ({ count = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} height={12} width={`${80 - i * 6}%`} />
    ))}
  </div>
);

const Overlay: React.FC<
  Pick<LoaderProps, "fullscreen" | "blur" | "label" | "size">
> = ({ fullscreen = false, blur = true, label, size = "lg" }) => (
  <div
    className={
      "pointer-events-none fixed inset-0 z-[60] flex items-center justify-center p-6" +
      (fullscreen ? "" : "")
    }
    aria-live="polite"
  >
    <div
      className={`absolute inset-0 ${
        blur ? "backdrop-blur-sm" : ""
      } bg-black/10 dark:bg-black/20`}
    />
    <div className="relative pointer-events-auto">
      <div className="rounded-2xl bg-background/90 p-4 shadow-xl ring-1 ring-border">
        <Spinner size={size} label={label || "Loading..."} />
      </div>
    </div>
  </div>
);

const ButtonLoader: React.FC<Pick<LoaderProps, "size" | "label">> = ({
  size = "sm",
  label,
}) => (
  <div
    className="flex items-center justify-center gap-2"
    role="status"
    aria-busy
  >
    <Spinner size={size} label={label} />
  </div>
);

const Loader: React.FC<LoaderProps> = ({
  variant = "spinner",
  show = true,
  size = "md",
  label,
  width,
  height,
  rounded,
  count = 3,
  fullscreen,
  blur,
  asChild,
  className,
  ...rest
}) => {
  if (!show) return null;

  const content = (() => {
    switch (variant) {
      case "spinner":
        return <Spinner size={size} label={label} />;
      case "dots":
        return <Dots size={size} label={label} />;
      case "bar":
        return <Bar size={size} label={label} />;
      case "skeleton":
        return (
          <div className="space-y-3">
            <Skeleton width={width} height={height} rounded={rounded} />
            <SkeletonBlock count={count} />
          </div>
        );
      case "overlay":
        return (
          <Overlay
            fullscreen={fullscreen}
            blur={blur}
            label={label}
            size={size}
          />
        );
      case "button":
        return <ButtonLoader size={size} label={label} />;
      default:
        return <Spinner size={size} label={label} />;
    }
  })();

  if (asChild) return <>{content}</>;

  return (
    <AnimatePresence>
      <motion.div
        {...rest}
        className={`flex items-center justify-center w-full py-12 min-h-[200px] ${className ?? ""}`}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.15 }}
      >
        {content}
      </motion.div>
    </AnimatePresence>
  );
};

export default Loader;

/* -------------------------------- USAGE ---------------------------------

import Loader, { Skeleton } from "@/components/common/Loader";

// 1) Page-level spinner
<Loader variant="spinner" size="lg" label="Fetching products..." />

// 2) Dots with label
<Loader variant="dots" label="Saving changes" />

// 3) Progress bar style
<Loader variant="bar" size="lg" label="Uploading" />

// 4) Skeletons
<Loader variant="skeleton" width="100%" height={160} count={4} />

// 5) Fullscreen overlay (block UI during critical actions)
{isLoading && (
  <Loader variant="overlay" fullscreen blur label="Please wait" />
)}

// 6) Inside a button
<button className="relative inline-flex items-center justify-center px-4 py-2 rounded-xl bg-black text-white">
  <span className={isLoading ? "opacity-0" : ""}>Pay now</span>
  {isLoading && (
    <span className="absolute inset-0 flex items-center justify-center">
      <Loader variant="button" size="sm" asChild />
    </span>
  )}
</button>

*/
